/**
 * PTV2-1157 — registry of card-level edits that are typed but not yet saved.
 *
 * Every editable record card commits explicitly: while its draft differs from the saved value it
 * renders a {@link DirtyFooter} with Save / Discard. That is deliberate (an accidental click must not
 * persist), but in the pedigree node editor it loses data: selecting another node — or clicking the
 * background to deselect — tears the panel down and the unsaved draft goes with it.
 *
 * Rather than teach every card about the pedigree, the shared footer registers itself here. The
 * footer only exists while a card is dirty, so "an entry exists" IS "something is unsaved", and any
 * card that adopts the shared footer is covered automatically — today's five and future ones alike.
 *
 * Consumers:
 *   - the node editor flushes the outgoing person's entries when the selection changes;
 *   - the pedigree's save (manual or autosave) flushes everything first;
 *   - a debounce flushes on its own if a draft is simply left sitting.
 *
 * `save()` is fire-and-forget: it starts the card's mutation, which is optimistic and invalidates the
 * person query on settle. We deliberately do not await — the point is that the edit is committed, not
 * that the caller blocks on the round-trip.
 */

/** A card whose draft differs from the saved record. */
export interface DirtyCardEntry {
  /**
   * What the edit belongs to — the person guid inside the pedigree node editor, `null` on the
   * patient-record page (where nothing tears the card down underneath the user).
   */
  readonly scopeId: string | null;
  /** Human-readable card name, for logging only. */
  readonly label: string;
  /** Commit the draft. Must be safe to call when already saving (the card guards its own mutation). */
  readonly save: () => void;
  /** Throw the draft away and snap back to the saved record — the card's own Discard action. */
  readonly discard: () => void;
  /**
   * Translated names of the fields that differ from the saved record — no values (PTV2-1157).
   * Names alone can't leak PHI into a dialog or a log line, and can't go stale the way a rendered
   * value could; the point is to tell the user WHAT they'd lose, not to re-display it.
   */
  readonly changedFields: () => readonly string[];
  /**
   * Is the user typing in this card right now? Cards re-sync their draft from the server once a save
   * settles, so committing mid-keystroke would reset the field and drop whatever was typed after the
   * flush. The idle flush therefore skips a focused card and waits; a flush caused by the node going
   * away does not (losing the panel is worse).
   */
  readonly isEditing: () => boolean;
  /**
   * PTV2-1301 — can this draft be committed right now? A create form with required fields still
   * empty cannot: calling {@link save} would no-op, so the unsaved-changes prompt disables Save and
   * warns instead of silently proceeding (which would lose the draft). Omitted ⇒ always saveable,
   * preserving the existing record cards' commit-anything behaviour.
   */
  readonly canSave?: () => boolean;
  /**
   * PTV2-1327 — this surface manages its OWN idle autosave (it's backed by `useEditableDraft`), so
   * the pedigree's legacy idle-flush must not also commit it. Teardown flushes (node-switch, deselect,
   * before-save) still commit it — only the idle timer defers. Absent/false ⇒ legacy behaviour (the
   * idle flush still autosaves it), which non-migrated surfaces rely on.
   */
  readonly autosaveOwned?: boolean;
  /**
   * PTV2-1327 — is this surface CURRENTLY autosaving (autosave-capable AND the global toggle is on)?
   * The unsaved-changes guard uses it: on a trigger it silently saves the autosaving surfaces (the
   * dialog would be redundant — the autosave already commits them) and only prompts for the rest.
   */
  readonly autosaveActive?: () => boolean;
}

type Listener = () => void;

let nextId = 1;
const entries = new Map<number, DirtyCardEntry>();
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Announce that a card is dirty. Returns the unregister function — call it when the card is no
 * longer dirty (i.e. from the footer's effect cleanup).
 */
export function registerDirtyCard(entry: DirtyCardEntry): () => void {
  const id = nextId++;
  entries.set(id, entry);
  notify();
  return () => {
    if (entries.delete(id)) {
      notify();
    }
  };
}

/** Is anything unsaved? Pass a scope to ask about one person only. */
export function hasDirtyCards(scopeId?: string | null): boolean {
  if (scopeId === undefined) return entries.size > 0;
  for (const entry of entries.values()) {
    if (entry.scopeId === scopeId) return true;
  }
  return false;
}

/**
 * PTV2-1301 — can every dirty draft in scope be committed? False when any registered entry reports
 * it can't (a create form missing required fields). Drives the unsaved-changes prompt: Save is
 * disabled and a warning shown, so the user must Discard or Keep editing rather than lose the draft.
 */
export function canSaveAllDirty(scopeId?: string | null): boolean {
  for (const entry of entries.values()) {
    if (scopeId !== undefined && entry.scopeId !== scopeId) continue;
    if (entry.canSave !== undefined && !entry.canSave()) return false;
  }
  return true;
}

/** Labels of the currently-dirty cards, for logging. */
export function describeDirtyCards(scopeId?: string | null): string[] {
  const out: string[] = [];
  for (const entry of entries.values()) {
    if (scopeId === undefined || entry.scopeId === scopeId) out.push(entry.label);
  }
  return out;
}

export interface FlushOptions {
  /** Only this person's cards. Omit for every scope. */
  readonly scopeId?: string | null;
  /**
   * Leave the card the user is currently typing in alone (see {@link DirtyCardEntry.isEditing}).
   * Use for the idle flush; never for a flush caused by the card being torn down.
   */
  readonly skipEditing?: boolean;
  /**
   * PTV2-1327 — skip surfaces that own their autosave ({@link DirtyCardEntry.autosaveOwned}). The
   * pedigree idle flush passes this so a `useEditableDraft`-backed surface autosaves on its OWN
   * (keystroke-reset, visible) debounce instead of the blunt 10 s idle flush. Teardown flushes leave
   * it off so leaving the node still commits everything.
   */
  readonly skipAutosaveOwned?: boolean;
}

/**
 * Commit dirty cards. Returns how many were actually flushed.
 *
 * Iterates a snapshot: saving flips a card out of its dirty state, which unregisters it mid-loop.
 */
export function flushDirtyCards(options: FlushOptions = {}): number {
  const { scopeId, skipEditing = false, skipAutosaveOwned = false } = options;
  const snapshot = [...entries.values()].filter(
    (entry) => scopeId === undefined || entry.scopeId === scopeId,
  );
  let flushed = 0;
  for (const entry of snapshot) {
    try {
      if (skipAutosaveOwned && entry.autosaveOwned === true) continue;
      if (skipEditing && entry.isEditing()) continue;
      entry.save();
      flushed++;
    } catch {
      // One card failing to commit must not strand the others; its own error handling surfaces it.
    }
  }
  return flushed;
}

/** One dirty card, as shown in the "unsaved changes" prompt. */
export interface DirtyCardSummary {
  readonly label: string;
  readonly fields: readonly string[];
}

/**
 * What is unsaved in a scope, for the confirm dialog shown when autosave is off.
 *
 * Read synchronously at the moment the user leaves the node — the cards re-render against the next
 * person immediately after, so anything read later would describe the wrong record.
 */
export function summarizeDirtyCards(scopeId?: string | null): DirtyCardSummary[] {
  const out: DirtyCardSummary[] = [];
  for (const entry of entries.values()) {
    if (scopeId !== undefined && entry.scopeId !== scopeId) continue;
    out.push({ label: entry.label, fields: [...entry.changedFields()] });
  }
  return out;
}

/**
 * A surface is auto-committed on a nav/teardown trigger iff it is autosaving AND currently
 * committable — otherwise its edit would be silently lost, so it must be prompted instead.
 */
function willAutoCommit(entry: DirtyCardEntry): boolean {
  return entry.autosaveActive?.() === true && (entry.canSave === undefined || entry.canSave());
}

/**
 * PTV2-1327 — commit NOW (synchronously, not on the debounce) every surface that will auto-commit in
 * scope. Called by the unsaved-changes guard on a navigation/teardown trigger so the pending
 * debounced edit is saved BEFORE the surface unmounts, instead of being lost. Returns how many were
 * flushed.
 */
export function flushAutosavingDirtyCards(scopeId?: string | null): number {
  const snapshot = [...entries.values()].filter(
    (entry) => (scopeId === undefined || entry.scopeId === scopeId) && willAutoCommit(entry),
  );
  let flushed = 0;
  for (const entry of snapshot) {
    try {
      entry.save();
      flushed++;
    } catch {
      // one card failing must not strand the others.
    }
  }
  return flushed;
}

/**
 * Is there a dirty surface in scope that will NOT auto-commit — i.e. one that genuinely needs the
 * Save / Discard / Keep-editing decision (a create/add form, a modal, an autosaving edit with a
 * required field still empty, or anything while autosave is off)? The guard prompts only when true.
 */
export function hasDirtyCardsNeedingPrompt(scopeId?: string | null): boolean {
  for (const entry of entries.values()) {
    if (scopeId !== undefined && entry.scopeId !== scopeId) continue;
    if (!willAutoCommit(entry)) return true;
  }
  return false;
}

/** Summaries of only the surfaces that need a prompt (what the dialog actually lists). */
export function summarizeDirtyCardsNeedingPrompt(scopeId?: string | null): DirtyCardSummary[] {
  const out: DirtyCardSummary[] = [];
  for (const entry of entries.values()) {
    if (scopeId !== undefined && entry.scopeId !== scopeId) continue;
    if (willAutoCommit(entry)) continue;
    out.push({ label: entry.label, fields: [...entry.changedFields()] });
  }
  return out;
}

/** Drop every dirty draft in a scope (the dialog's Discard). Returns how many were discarded. */
export function discardDirtyCards(options: FlushOptions = {}): number {
  const { scopeId } = options;
  const snapshot = [...entries.values()].filter(
    (entry) => scopeId === undefined || entry.scopeId === scopeId,
  );
  let discarded = 0;
  for (const entry of snapshot) {
    try {
      entry.discard();
      discarded++;
    } catch {
      // As with flushing: one card must not strand the others.
    }
  }
  return discarded;
}

/** Subscribe to dirty/clean transitions (used to drive the idle flush timer). */
export function subscribeDirtyCards(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
